// src/communaute/CommentsModal.jsx
//
// Commentaires d'une publication : racines paginées + réponses (1 niveau).

import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, Reply, Send, Trash2, X } from 'lucide-react';

import { ajouterComment, getComments, getReplies, monId, supprimerComment } from './api';
import { tempsRelatif } from './normalize';
import { Avatar, Chargement, Modal, Spinner, Vide, champ, toast, useScrollInfini } from './ui';

export default function CommentsModal({ post, ouvert, onFermer, onCompte }) {
  const [comments, setComments] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [chargementPlus, setChargementPlus] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(post.nbrComments || 0);

  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [repondA, setRepondA] = useState(null);
  const [repliesEnCours, setRepliesEnCours] = useState({});
  const inputRef = useRef(null);
  const moi = monId();

  // ── Chargement initial ──
  useEffect(() => {
    if (!ouvert) return;
    let annule = false;
    (async () => {
      setChargement(true);
      const r = await getComments(post.id, 1);
      if (annule) return;
      setComments(r.items.map((c) => ({ ...c, repliesPage: 1, repliesOuvertes: false })));
      setPage(r.page);
      setLastPage(r.lastPage);
      setTotal(r.total || post.nbrComments || 0);
      setChargement(false);
    })();
    return () => {
      annule = true;
    };
  }, [ouvert, post.id, post.nbrComments]);

  const chargerPlus = useCallback(async () => {
    if (chargementPlus || page >= lastPage) return;
    setChargementPlus(true);
    const r = await getComments(post.id, page + 1);
    setComments((l) => [...l, ...r.items.map((c) => ({ ...c, repliesPage: 1, repliesOuvertes: false }))]);
    setPage(r.page);
    setLastPage(r.lastPage);
    setChargementPlus(false);
  }, [chargementPlus, page, lastPage, post.id]);

  const sentinelle = useScrollInfini(chargerPlus, ouvert && page < lastPage);

  const majCompte = (n) => {
    const v = Math.max(0, n);
    setTotal(v);
    onCompte?.(v);
  };

  // ── Réponses ──
  const chargerReplies = async (c) => {
    if (repliesEnCours[c.id]) return;
    setRepliesEnCours((s) => ({ ...s, [c.id]: true }));
    const p = c.repliesOuvertes ? c.repliesPage + 1 : 1;
    const r = await getReplies(c.id, p);
    setComments((l) =>
      l.map((x) =>
        x.id !== c.id
          ? x
          : {
            ...x,
            replies: p === 1 ? r.items : [...x.replies, ...r.items],
            repliesPage: r.page,
            hasMoreReplies: r.page < r.lastPage,
            repliesOuvertes: true,
          },
      ),
    );
    setRepliesEnCours((s) => ({ ...s, [c.id]: false }));
  };

  const replier = (c) =>
    setComments((l) =>
      l.map((x) =>
        x.id !== c.id ? x : { ...x, replies: x.replies.slice(0, 3), repliesOuvertes: false, repliesPage: 1 },
      ),
    );

  // ── Envoi ──
  const envoyer = async (e) => {
    e?.preventDefault();
    const t = texte.trim();
    if (!t || envoi) return;

    setEnvoi(true);
    const c = await ajouterComment(post.id, t, repondA?.id);
    setEnvoi(false);

    if (!c) {
      toast("Échec de l'envoi. Réessayez plus tard.");
      return;
    }

    if (repondA) {
      setComments((l) =>
        l.map((x) =>
          x.id !== repondA.id
            ? x
            : { ...x, replies: [...x.replies, c], nbrReplies: x.nbrReplies + 1, repliesOuvertes: true },
        ),
      );
    } else {
      setComments((l) => [{ ...c, repliesPage: 1, repliesOuvertes: false }, ...l]);
    }
    majCompte(total + 1);
    setTexte('');
    setRepondA(null);
  };

  // ── Suppression ──
  const supprimer = async (c, parent = null) => {
    if (!(await supprimerComment(c.id))) {
      toast('Échec de la suppression');
      return;
    }
    if (parent) {
      setComments((l) =>
        l.map((x) =>
          x.id !== parent.id
            ? x
            : { ...x, replies: x.replies.filter((r) => r.id !== c.id), nbrReplies: Math.max(0, x.nbrReplies - 1) },
        ),
      );
      majCompte(total - 1);
    } else {
      setComments((l) => l.filter((x) => x.id !== c.id));
      majCompte(total - 1 - c.nbrReplies); // les réponses partent en cascade
    }
  };

  const estAMoi = (c) => c.isMine || (moi > 0 && c.auteur.id === moi);

  const ligne = (c, estReponse, parent) => (
    <div className="group flex items-start gap-3">
      <Avatar src={c.auteur.photo} taille={estReponse ? 28 : 36} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className={`truncate font-semibold text-gray-900 ${estReponse ? 'text-[13px]' : 'text-sm'}`}>
            {c.auteur.nom}
          </span>
          <span className="shrink-0 text-[11px] text-gray-400">{c.dateTexte || tempsRelatif(c.date)}</span>
        </div>
        <p className={`mt-1 whitespace-pre-line break-words text-gray-700 ${estReponse ? 'text-[13px]' : 'text-sm'}`}>
          {c.contenu}
        </p>
        {!estReponse && (
          <button
            onClick={() => {
              setRepondA(c);
              inputRef.current?.focus();
            }}
            className="mt-1.5 text-xs font-semibold text-gray-500 hover:text-gray-900"
          >
            Répondre
          </button>
        )}
      </div>
      {estAMoi(c) && (
        <button
          onClick={() => supprimer(c, parent)}
          className="rounded-full p-1.5 text-gray-300 opacity-0 transition hover:bg-gray-100 hover:text-red-600 group-hover:opacity-100"
          title="Supprimer"
        >
          <Trash2 size={estReponse ? 14 : 16} />
        </button>
      )}
    </div>
  );

  return (
    <Modal
      ouvert={ouvert}
      onFermer={onFermer}
      titre={total ? `Commentaires (${total})` : 'Commentaires'}
      taille="md"
      pied={
        <div>
          {repondA && (
            <div className="-mx-5 -mt-4 mb-3 flex items-center gap-2 bg-[#7C3AED]/15 px-5 py-2 text-[13px] text-gray-700">
              <Reply size={15} className="text-[#9B59B6]" />
              <span className="truncate">Réponse à {repondA.auteur.nom}</span>
              <button onClick={() => setRepondA(null)} className="ml-auto text-gray-500 hover:text-gray-900">
                <X size={16} />
              </button>
            </div>
          )}
          <form onSubmit={envoyer} className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) envoyer(e);
              }}
              rows={1}
              placeholder={repondA ? 'Votre réponse…' : 'Écrire un commentaire…'}
              className={`${champ} max-h-32 resize-none rounded-3xl`}
            />
            <button
              type="submit"
              disabled={!texte.trim() || envoi}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] text-white disabled:opacity-40"
            >
              {envoi ? <Spinner taille={18} className="!text-white" /> : <Send size={18} />}
            </button>
          </form>
        </div>
      }
    >
      {chargement ? (
        <Chargement />
      ) : comments.length === 0 ? (
        <Vide icone={MessageCircle} titre="Aucun commentaire" sous="Soyez le premier à réagir" />
      ) : (
        <div className="space-y-5 px-5 py-4">
          {comments.map((c) => (
            <div key={c.id}>
              {ligne(c, false)}

              {c.replies.length > 0 && (
                <div className="ml-12 mt-3 space-y-3">
                  {c.replies.map((r) => (
                    <div key={r.id}>{ligne(r, true, c)}</div>
                  ))}
                </div>
              )}

              {c.nbrReplies > 0 && (
                <button
                  disabled={repliesEnCours[c.id]}
                  onClick={() => (c.repliesOuvertes && !c.hasMoreReplies ? replier(c) : chargerReplies(c))}
                  className="ml-12 mt-2 flex items-center gap-2 text-[12.5px] font-semibold text-[#9B59B6] hover:text-[#b57ad0]"
                >
                  <span className="h-px w-5 bg-gray-300" />
                  {repliesEnCours[c.id] ? (
                    <Spinner taille={13} />
                  ) : c.repliesOuvertes && !c.hasMoreReplies ? (
                    'Masquer les réponses'
                  ) : c.repliesOuvertes ? (
                    'Voir plus de réponses'
                  ) : (
                    `Voir les ${c.nbrReplies} réponse${c.nbrReplies > 1 ? 's' : ''}`
                  )}
                </button>
              )}
            </div>
          ))}
          <div ref={sentinelle} />
          {chargementPlus && (
            <div className="flex justify-center py-2">
              <Spinner taille={20} />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}